import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/sonner'
import { INPUT_CLASSES } from '@/lib/theme'
import athleteApi from '@/lib/api'
import { useGetMyMarketplaceProfileQuery } from '@/redux/collaborationsApi'
import { useAuth } from '@/hooks/useAuth'
import SetupShell, { SetupCard, SectionTitle } from '@/components/setup/SetupShell'

/** A starting username from the athlete's name: "Riley Chen" -> "rileychen" */
const suggestUsername = (name?: string | null) =>
  (name ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 30)

/**
 * Open your Content Shop: pick a username and publish in one tap, instead of the full
 * public-profile editor (which stays available too).
 */
const PublishProfile: React.FC = () => {
  const navigate = useNavigate()
  const { athlete, refreshUser } = useAuth()
  const existing = athlete?.public_profile_username ?? null
  const published = !!athlete?.public_profile_published
  const [username, setUsername] = useState(existing ?? suggestUsername(athlete?.name))
  const [saving, setSaving] = useState(false)

  // The shop's address in the brand app, from the shop itself (it differs per environment)
  const { data: shop, refetch } = useGetMyMarketplaceProfileQuery()
  const shopBase = (shop?.shopUrl ?? '')
    .replace(/\/shop\/[^/]+$/, '/shop/')
    .replace(/^https?:\/\//, '')

  const clean = username.toLowerCase().replace(/[^a-z0-9_-]/g, '')
  const valid = clean.length >= 3

  const publish = async () => {
    if (!valid) return
    setSaving(true)
    try {
      if (!existing) {
        await athleteApi.post('/athlete/profile/public', { username: clean })
      } else if (existing !== clean) {
        await athleteApi.patch('/athlete/profile/public', { username: clean })
      }
      await athleteApi.post('/athlete/profile/public/publish')
      await refreshUser()
      refetch()
      toast('Your Content Shop is open', {
        description: shopBase ? `${shopBase}${clean}` : 'Brands can find you and book.',
      })
      navigate('/brand-view')
    } catch (err: any) {
      toast.error('Could not publish', {
        description: err?.response?.data?.error ?? 'Please try again.',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <SetupShell
      title="Publish"
      subtitle={
        <div>
          <SectionTitle>Open your Content Shop</SectionTitle>
          <p className="mt-1 text-sm text-gray-600">
            Your Content Shop is where brands browse what you make and book it.
          </p>
        </div>
      }
      footer={
        <Button
          variant="lime"
          className="h-12 w-full text-base"
          onClick={published && existing === clean ? () => navigate('/brand-view') : publish}
          disabled={saving || !valid}
        >
          {saving
            ? 'Opening…'
            : published && existing === clean
              ? 'Back to your Content Shop'
              : 'Open my shop'}
        </Button>
      }
      footnote="You can change your page any time."
    >
      <SetupCard className="space-y-2">
        <Label htmlFor="username" className="font-bold text-foreground">
          Your shop name
        </Label>
        <div className="flex items-center rounded-md border border-foreground/10 bg-card pl-3">
          <span className="shrink-0 text-gray-500">shop/</span>
          <Input
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className={`${INPUT_CLASSES.dark} border-0 bg-transparent pl-0.5 focus-visible:ring-0`}
            autoCapitalize="none"
            autoCorrect="off"
            maxLength={30}
          />
        </div>
        <p className="text-xs text-gray-500">
          Letters, numbers, dashes and underscores. At least 3 characters.
        </p>
        <p className="break-all text-xs text-gray-600">
          Your shop, for brands: {shopBase || 'athios.ai/shop/'}
          {clean || 'yourname'}
          <br />
          Your link in bio: athios.ai/p/{clean || 'yourname'}
        </p>
        {published ? (
          <p className="flex items-center gap-1.5 text-xs font-semibold text-[#4d7c0f]">
            <Check className="h-3.5 w-3.5" strokeWidth={3} /> Open at{' '}
            {shopBase || 'athios.ai/shop/'}
            {existing}
          </p>
        ) : null}
      </SetupCard>
    </SetupShell>
  )
}

export default PublishProfile
